import EditIcon from '@material-ui/icons/EditOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { headerCase } from 'change-case';
import React from 'react';
import { isNullOrUndefined, LQ, match, UTC, utc } from 'rewire-common';
import { createWatcherFn, freeze, version, WatcherTypeFn } from 'rewire-core';
import { createColumn, createGrid, ICell, IColumn, IGrid, IRow } from 'rewire-grid';
import { arraySearch, defaultMap, ErrorSeverity, IError, IToggleMenuItem } from 'rewire-ui';
import {
  DeletedScopeId,
  EntityType,
  eTableType,
  FieldFilter,
  IRuleSetTable,
  ITableSchemaColumn,
  IUdf,
  UdfDocumentType,
} from '../DataTypes';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import mainPageModel from '../main-navigation/MainPageModel';
import addReasonDialogModel from '../schedule/AddReasonDialogModel';
import hoursEarnedTypeDialogModel from '../schedule/HoursEarnedTypeDialogModel';
import scheduleRuleDialogModel from '../schedule/ScheduleRuleDialogModel';
import udfsSubscriptionService from '../services/UdfsSubscriptionService';
import {
  AdvancedAutoCompleteActionType,
  AdvancedAutoCompleteSuggestionActionType,
  getAdvancedAutoCompleteOptions,
} from '../ui/AdvancedAutoComplete';
import { notifications } from '../ui/Notifications';
import { createUdfSearch, getArrayString, getTemplateArrayString, ISearchFn } from '../utility/Searchers';
import { createExpressionEditor } from './ExpressionEditor';
import { createEditor, MultiSelectAutoCompleteRenderer, RuleSettingsEditor } from './RuleSettingsEditor';
import SettingsModel from './SettingsModel';

const defaultNameSearchReturnSchema = `
  id
  name
  code
  display
`;

type ReferenceType =
  | 'employees'
  | 'batches'
  | 'areas'
  | 'groups'
  | 'occupations'
  | 'reasons'
  | 'generators'
  | 'templates'
  | 'users'
  | 'categories'
  | 'documents'
  | 'hoursEarnedTypes'
  | 'views';

const timecardRuleSetId = 'vqBfBC2jXvuFQGqtiyYhkP';
function toBoolean(obj: any): string {
  if (!obj) return 'false';
  if (typeof obj === 'string') {
    switch (obj.toLowerCase()) {
      case 'false':
      case '0':
      case 'no':
        return 'false';
      case 'true':
      case '1':
      case 'yes':
        return 'true';
    }
  }
  return 'true';
}

export function columnSearcher<T>(grid: IGrid, columnName: string, map?: (item?: T) => string, _value?: string) {
  const _map = map || defaultMap;

  function getSuggestions(value: string): string[] {
    let count = 0;
    const regex = match(value, 'i');
    value = value?.trim() ?? '';
    const suggestions: string[] = LQ(grid.rows)
      .map((r) => r.data[columnName])
      .flatMap((v) => (Array.isArray(v) ? v : [v]))
      .map((v) => v?.trim())
      .concat([value])
      .filter((f) => !!f)
      .distinct()
      .filter((suggestion) => {
        if (value.length === 0) return true;
        if (suggestion === value) return true;
        const keep = count < 8 && regex.test(_map(suggestion as any));

        if (keep) {
          count += 1;
        }

        return keep;
      })
      .value();
    suggestions.sort((a, b) => (a === value ? -1 : b === value ? 1 : a.localeCompare(b)));
    return suggestions;
  }

  function search(searchText: string): Promise<string[]> {
    return Promise.resolve(getSuggestions(searchText));
  }

  return { map: _map, search };
}

export default class RuleSettingsEditorModel extends SettingsModel<{
  table: IRuleSetTable | undefined;
  availableUdfs: IToggleMenuItem[];
}> {
  readonly grid: IGrid;
  _ruleSetId: string;
  columns: [string, ITableSchemaColumn][] = [];
  watchUdfs: WatcherTypeFn;
  ToolbarButtons?: React.ComponentType<{ settings: RuleSettingsEditorModel }>;

  constructor(title = 'Timecard', ruleSetId: string = timecardRuleSetId) {
    super(title);
    this.SettingsView = RuleSettingsEditor;
    this.grid = createGrid([], [], { variableKeybinds: this.keyBindings, clearSelectionOnBlur: false });
    this.grid.rowHeight = 38;
    this.grid.headerRowHeight = 28;
    this.grid.isRowCompleteFn = this.isRowComplete;
    this._ruleSetId = ruleSetId;
    this.state.table = undefined;
    this.state.availableUdfs = [];

    this.watchUdfs = createWatcherFn();
    this.watchUdfs(
      () => {
        version(this.udfs);
        this.table && version(this.table.schemaDefn);
      },
      () => {
        this.setAvailableUdfs();
      },
    );
  }

  private setAvailableUdfs() {
    const udfs: IToggleMenuItem[] = [];
    if (this.table?.schemaDefn && this.udfs.length > 0) {
      const schema = this.table.schemaDefn.schema;
      let group: string | undefined = undefined;
      for (const udf of this.udfs) {
        let subheader: string | undefined;
        if (!group || udf.documentType !== group) {
          subheader = headerCase(udf.documentType);
        }
        udfs.push({ subheader, name: udf.name, title: udf.displayName, active: !!schema[udf.name] });
        group = udf.documentType;
      }
    }
    this.state.availableUdfs = udfs;
  }

  private keyBindings = {
    '*': (evt: React.KeyboardEvent<any>, cell: ICell) => {
      cell.value = '*';
      evt.stopPropagation();
      evt.preventDefault();
    },
    'Shift+*': (evt: React.KeyboardEvent<any>, cell: ICell) => {
      cell.value = '*';
      evt.stopPropagation();
      evt.preventDefault();
    },
    Delete: (evt: React.KeyboardEvent<any>, cell: ICell) => {
      cell.value = undefined;
      evt.stopPropagation();
      evt.preventDefault();
    },
  };

  get access(): boolean {
    return mainPageModel.timecardRulesAccess;
  }

  get viewOnly(): boolean {
    return !this._canAddItem && !this._canEditItem;
  }

  get _canAddItem(): boolean {
    return mainPageModel.canEditTimecardRules;
  }

  get _canEditItem(): boolean {
    return mainPageModel.canEditTimecardRules;
  }

  get _canDeleteItem(): boolean {
    return mainPageModel.canEditTimecardRules;
  }

  get udfs(): IUdf[] {
    const employeeUdfs = udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('employees');
    const timecardUdfs = udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('timecards');
    // const shiftTypes   = udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('templates');
    // const jobs         = udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('jobs');
    // return [...employeeUdfs, ...timecardUdfs, ...shiftTypes, ...jobs];
    return [...employeeUdfs, ...timecardUdfs];
  }

  get titleText() {
    return `${this.title} Rules${this.selectedItem ? ` - ${this.selectedItem.name}` : ''}`;
  }

  createUdfColumn(udf: IUdf, columnDefn: ITableSchemaColumn) {
    switch (udf.type) {
      case 'reference': {
        const options = createUdfSearch(udf) as any;
        const column = createColumn(udf.name, udf.displayName, {
          type: { type: 'multiselectautocomplete', options },
          renderer: MultiSelectAutoCompleteRenderer(),
          map: getArrayString,
        });
        column.editor = createEditor(options);
        return column;
      }

      case 'date':
        return createColumn(udf.name, udf.displayName, {
          type: 'text',
          validators: this.validator(columnDefn),
          align: this.getAlignmentForType(columnDefn.type),
        });

      case 'text': {
        const options = createUdfSearch(udf, (v) => v.name) as any;
        const column = udf.allowableValues
          ? udf.isList
            ? createColumn(udf.name, udf.displayName, {
                type: { type: 'multiselectautocomplete', options },
                renderer: MultiSelectAutoCompleteRenderer(),
                map: getArrayString,
              })
            : createColumn(udf.name, udf.displayName, {
                type: { type: 'select', options },
                renderer: MultiSelectAutoCompleteRenderer(),
                map: getArrayString,
              })
          : createColumn(udf.name, udf.displayName, {
              type: 'text',
              validators: this.validator(columnDefn),
              align: this.getAlignmentForType(columnDefn.type),
            });
        if (udf.allowableValues && udf.isList) column.editor = createEditor(options);
        return column;
      }

      case 'boolean':
      case 'integer':
      case 'float': {
        const column = createColumn(udf.name, udf.displayName, {
          type: 'text',
          validators: this.validator(columnDefn),
          align: this.getAlignmentForType(columnDefn.type),
        });
        if (columnDefn.type === 'number') column.onValueChange = this.convert;
        if (udf.type === 'boolean') column.onValueChange = this.convertBoolean;
        return column;
      }
    }
  }

  toggleUdfColumn(name: string) {
    const udf = this.udfs.find((f) => f.name === name);
    if (!udf || !(this.table?.schemaDefn && this.udfs.length > 0)) return;

    const schema = this.table.schemaDefn.schema;
    const existing = schema[name];
    if (existing) {
      if (existing.type === 'udf') {
        for (const r of this.table.rows) delete r[name];
        delete schema[name];
      }
    } else {
      let lastMatchColumnBeforeResult: ITableSchemaColumn | undefined = undefined;
      for (const c of Object.values(schema).sort((a, b) => a.order - b.order)) {
        if (c.columnType === 'result') break;
        lastMatchColumnBeforeResult = c;
      }
      const order = (lastMatchColumnBeforeResult?.order ?? 0) + 0.1;
      schema[name] = {
        columnType: 'match',
        multiSelect: true,
        name: udf.displayName,
        type: 'udf',
        width: '120px',
        order,
        typeOptions: { referenceType: udf.documentType },
      };
      for (const r of this.table.rows) r[name] = '*';
    }
    this.setAvailableUdfs();
    this.recreateGrid(false);
  }

  get availableUdfs(): IToggleMenuItem[] {
    return this.state.availableUdfs;
  }

  get tables() {
    return this.state.items;
  }

  get table(): IRuleSetTable | undefined {
    return this.state.table;
  }

  set table(value: IRuleSetTable | undefined) {
    this.state.table = value;
  }

  isRowComplete = (row: IRow) => {
    return (
      row?.data &&
      ((row.data.category && (row.data.category.id || typeof row.data.category === 'string')) ||
        (row.data.holiday && row.data.holiday !== '*') ||
        (row.data.segment && row.data.segment !== '*'))
    );
  };

  async validateSchema() {
    const schema = this.table?.schemaDefn.schema;
    if (!schema) return;
    for (const [k, value] of Object.entries(schema)) {
      if (value.type === 'udf') {
        const udf = this.udfs.find((v) => v.name === k);
        if (!udf) delete schema[k];
      }
    }
  }

  canSave() {
    return this.table && this.hasChanges && !this.viewOnly;
  }

  async save() {
    if (!this.canSave()) return;
    this.validateSchema();
    const rows = LQ(this.grid.rows)
      .filter(this.isRowComplete)
      .map((r) => r.data)
      .value();
    await DataMutations.saveRuleTable({
      schemaDefn: this.table!.schemaDefn,
      rows,
    });
    this.grid.commit();
    notifications.success(`${this.table!.schemaDefn.name} has been successfully saved`);
    return true;
  }

  checkForAddRow = () => {
    for (const row of this.grid.rows) {
      if (!this.isRowComplete(row)) return;
    }

    const data: any = {};
    for (const [column, columnSchema] of this.columns) {
      if (column === 'category' || columnSchema.columnType === 'result') continue;
      data[column] = '*';
    }

    this.grid.addRow({ id: String(this.grid.rows.length), data });
  };

  convert = (cell: ICell, value: any) => {
    if (!this.table) return;
    const columnSchema = this.table.schemaDefn.schema[cell.column.name];
    if (!columnSchema) return;
    if (columnSchema.type === 'number') {
      const v = Number(value);
      if (Number.isNaN(v) || typeof v !== 'number') return;
      cell.value = v;
    }
  };

  convertBoolean = (cell: ICell, value: any) => {
    if (!this.table) return;
    cell.value = value === undefined || value === '*' ? '*' : toBoolean(value);
  };

  async load() {
    this.setItems(await this.loadRuleSet());
    this.grid.enabled = !this.viewOnly;
  }

  async loadRuleSet() {
    return (this._ruleSetId && (await DataQueries.getRuleSet(this._ruleSetId))) || [];
  }

  async loadRuleTable(tableId: string) {
    return await DataQueries.getRuleTable(tableId);
  }

  validator =
    (columnDefn: ITableSchemaColumn) =>
    (value: any): IError | undefined => {
      if (!columnDefn || isNullOrUndefined(value)) {
        return undefined;
      }
      if (columnDefn.columnType === 'match' && value === '*') return undefined;

      if (columnDefn.type === 'number') {
        const v = Number(value);
        if (Number.isNaN(v) || typeof v !== 'number') {
          return { text: 'the specified value must be a number', severity: ErrorSeverity.Error };
        }
        return undefined;
      }

      if (columnDefn.type === 'date') {
        const v = String(value);
        if (v !== '*') {
          if (!utc(v).isValid) return { text: 'the specified value is not a date', severity: ErrorSeverity.Error };
        }
        return undefined;
      }

      return undefined;
    };

  protected createReferenceSearch(type: ReferenceType, typeOptions?: any, listMapper?: (v: any) => any) {
    let filter: FieldFilter | undefined = undefined;
    let mapper: (item: any) => any = getArrayString;

    switch (type) {
      case 'categories':
        filter = { field: 'category', eq: this.table?.schemaDefn.id };
        break;

      case 'hoursEarnedTypes':
        filter = { field: 'type', eq: 'hoursEarnedType' };
        type = 'documents';
        break;

      case 'templates':
        mapper = getTemplateArrayString;
        break;

      case 'reasons':
        filter = typeOptions.filter;
        break;

      case 'views':
        filter = typeOptions.filter;
        break;
    }

    return {
      async search(search?: string) {
        const r = await DataQueries.getSearch(defaultNameSearchReturnSchema, type as EntityType, search, filter);
        if (Array.isArray(r)) r.unshift({ id: DeletedScopeId, name: '«empty»', display: '«empty»' });
        if (r && listMapper) {
          return r.map(listMapper);
        }
        return r;
      },
      map(item: any) {
        if (Array.isArray(item)) return '';
        return mapper(item);
      },
    };
  }

  protected createTagSearch(searchFn: ISearchFn, type: ReferenceType, _listMapper?: (v: any) => any) {
    // let filter: FieldFilter | undefined = undefined;
    const mapper: (item: any) => any = getArrayString;

    return {
      async search(search?: string) {
        const result = new Set(searchFn ? await searchFn(search) : []);
        const r = await DataQueries.getTags(type as UdfDocumentType, search);
        for (const item of r) {
          result.add(item);
        }
        return Array.from(result);
      },
      map(item: any) {
        if (Array.isArray(item)) return '';
        return mapper(item);
      },
    };
  }

  protected getOptions(type: ReferenceType, typeOptions: any) {
    const search = this.createReferenceSearch(type, typeOptions);
    switch (type) {
      case 'hoursEarnedTypes': {
        const hoursEarnedAction: AdvancedAutoCompleteActionType = {
          action: this.showHoursEarnedTypeDialog,
          label: 'Add Hours Earned',
        };
        return getAdvancedAutoCompleteOptions('Hours Earned', hoursEarnedAction, undefined, search);
      }

      case 'reasons': {
        const reasonAction: AdvancedAutoCompleteActionType = { action: this.showReasonDialog, label: 'Add Reason Code' };
        return getAdvancedAutoCompleteOptions('Reason', reasonAction, undefined, search);
      }

      case 'views': {
        if (typeOptions?.filter?.field === 'type' && typeOptions?.filter?.eq === 'schedulingRule') {
          const schedulingRuleAction: AdvancedAutoCompleteActionType = {
            action: () => this.showScheduleRuleDialog(),
            label: 'Add Scheduling Rule',
            disabled: () => this.viewOnly,
          };
          const schedulingRuleSuggestionAction: AdvancedAutoCompleteSuggestionActionType = {
            action: (schedulingRuleId: string) => this.showScheduleRuleDialog(schedulingRuleId),
            icon: !this.viewOnly ? EditIcon : VisibilityIcon,
          };
          return getAdvancedAutoCompleteOptions('Scheduling Rule', schedulingRuleAction, schedulingRuleSuggestionAction, search);
        }
        return search;
      }

      default:
        return search;
    }
  }

  showHoursEarnedTypeDialog = () => {
    hoursEarnedTypeDialogModel.load();
  };

  showReasonDialog = () => {
    addReasonDialogModel.load();
  };

  showScheduleRuleDialog = (scheduleRuleId?: string) => {
    if (scheduleRuleId === DeletedScopeId) {
      return;
    }

    scheduleRuleDialogModel.load({
      scheduleRuleContext: undefined,
      scheduleRuleId: scheduleRuleId,
      earnedDate: UTC.now().startOfDay(),
    });
  };

  canUndo() {
    return this.hasChanges && !this.viewOnly;
  }

  undo() {
    if (!this.canUndo()) return;
    this.recreateGrid();
    this.checkForAddRow();
  }

  sort<T extends { order: number }[]>(schema: T) {
    const result = Object.entries(schema)
      .sort(([_key1, columnSchema1], [_key2, columnSchema2]) => columnSchema1.order - columnSchema2.order)
      .reduce((previous, [key, value]) => {
        previous[key] = value;
        return previous;
      }, {} as any) as T;
    return result;
  }

  setGridStructureData(newColumns?: IColumn[]) {
    freeze(() => {
      this.grid.validator.reset();
      this.grid.rows.set([]);
      this.grid.fixedRows.set([]);
      this.grid.spliceColumns(0, this.grid.columns.length);
    });
    freeze(() => {
      this.grid.addFixedRow({ data: {} });
      if (newColumns?.length) {
        this.grid.spliceColumns(0, 0, ...newColumns);
      }
    });
  }

  recreateGrid(changeTracking = true) {
    const rows = this.table?.rows;
    let resultColumnClass = false;
    changeTracking && this.grid.setChangeTracking(false);

    if (!this.table) {
      this.setGridStructureData();
      this.columns = [];
      return;
    }

    const columns: IColumn[] = [];
    this.columns = Object.entries(this.table.schemaDefn.schema).sort(
      ([_key1, columnSchema1], [_key2, columnSchema2]) => columnSchema1.order - columnSchema2.order,
    );

    for (const [column, columnDefn] of this.columns) {
      let gridColumn: IColumn;
      switch (columnDefn.type) {
        case 'reference': {
          const typeOptions = columnDefn.typeOptions;
          const referenceType = typeOptions.referenceType as ReferenceType;
          const options = this.getOptions(referenceType, typeOptions);
          const map = referenceType === 'templates' ? getTemplateArrayString : getArrayString;
          const renderer = MultiSelectAutoCompleteRenderer((referenceType === 'templates' && getTemplateArrayString) || undefined);
          if (columnDefn.multiSelect) {
            gridColumn = createColumn(column, columnDefn.name, {
              type: { type: 'multiselectautocomplete', options: options },
              renderer: renderer,
              map: map,
            });
            gridColumn.editor = createEditor(options);
          } else {
            gridColumn = createColumn(column, columnDefn.name, { type: { type: 'auto-complete', options }, renderer: renderer });
          }
          break;
        }

        case 'expression':
          gridColumn = createColumn(column, columnDefn.name, { type: 'text' });
          gridColumn.editor = createExpressionEditor((columnDefn.typeOptions as any)?.context);
          break;

        case 'udf': {
          const udf = this.udfs.find((udf) => udf.name === column);
          if (!udf) continue;
          gridColumn = this.createUdfColumn(udf, columnDefn)!;
          break;
        }

        case 'tag': {
          const typeOptions = columnDefn.typeOptions;
          const referenceType = typeOptions.referenceType as ReferenceType;
          const options = this.createTagSearch(columnSearcher(this.grid, column).search, referenceType);
          const map = getArrayString;
          const renderer = MultiSelectAutoCompleteRenderer();
          if (columnDefn.multiSelect) {
            gridColumn = createColumn(column, columnDefn.name, {
              type: { type: 'multiselectautocomplete', options: options },
              renderer: renderer,
              map: map,
            });
            gridColumn.editor = createEditor(options);
          } else {
            gridColumn = createColumn(column, columnDefn.name, { type: { type: 'auto-complete', options }, renderer: renderer });
          }
          break;
        }

        default:
          if (columnDefn.typeOptions?.allowableValues) {
            gridColumn = createColumn(column, columnDefn.name, {
              type: { type: 'auto-complete', options: arraySearch(columnDefn.typeOptions.allowableValues, (i) => i || '') },
            });
          } else if (!columnDefn.multiSelect && columnDefn.type === 'string') {
            gridColumn = createColumn(column, columnDefn.name, {
              type: { type: 'auto-complete', options: columnSearcher(this.grid, column, (i: any) => i || '«empty»') },
            });
          } else {
            gridColumn = createColumn(column, columnDefn.name, {
              type: 'text',
              validators: this.validator(columnDefn),
              align: this.getAlignmentForType(columnDefn.type),
            });
            if (columnDefn.type === 'number') gridColumn.onValueChange = this.convert;
          }
          break;
      }

      if (column === 'category' || column === 'holiday' || column === 'segment') {
        gridColumn.onValueChange = this.checkForAddRow;
      }

      if (columnDefn.width) gridColumn.width = columnDefn.width;
      if (columnDefn.columnType === 'result' && !resultColumnClass) {
        gridColumn.cls = 'result';
        resultColumnClass = true;
      }
      columns.push(gridColumn);
    }

    this.setGridStructureData(columns);

    if (rows && rows.length > 0) {
      this.grid.addRows(rows.map((r, i) => ({ id: String(i), data: r })));
    }
    this.checkForAddRow();

    changeTracking && this.grid.setChangeTracking(true);
  }

  getAlignmentForType(type: eTableType): 'left' | 'center' | 'right' | undefined {
    if (type === 'number') return 'right';
    return 'left';
  }

  get hasChanges() {
    return this.grid.hasChanges;
  }

  async select(selectedItemId?: string | null): Promise<void> {
    this.isLoading = true;
    if (!this._settingsState.selected) {
      await this.load();
      this.activate();
      this._settingsState.selected = true;
    }

    this.updateTitle();
    if (
      (!selectedItemId && !this.selectedItem) ||
      (selectedItemId && this.selectedItem && this.selectedItem.id === selectedItemId)
    ) {
      this.isLoading = false;
      return;
    }
    this.table = selectedItemId ? await this.loadRuleTable(selectedItemId) : undefined;
    this._selectItem(this.state.items.find((f) => f.id === selectedItemId));
    this.recreateGrid();
    this.checkForAddRow();
    this.isLoading = false;
  }

  deactivate(): void {}
  activate(): void {}
}
